// TeamStats.jsx

// styling
import styles from './styles.module.scss';

// components
import Spring from '@components/Spring';
import StatsBadge from '@ui/StatsBadge';

// hooks
import { useThemeProvider } from '@contexts/themeContext';
import { useWindowSize } from 'react-use';

// assets (fallbacks)
import fallbackLogo from '@assets/clubs/bvb.webp';
import fallbackCover from '@assets/team_stats.webp';

const normalizeStats = (stats) => {
  if (!stats) return [];
  if (Array.isArray(stats)) {
    return stats.map(s => ({
      label: s.label,
      shortLabel: s.shortLabel ?? (s.label?.[0]?.toUpperCase() || ''),
      value: Number(s.value) || 0,
    }));
  }
  const w = stats.wins ?? stats.W ?? stats.w ?? stats.win;
  const d = stats.draws ?? stats.D ?? stats.d ?? stats.draw;
  const l = stats.losses ?? stats.L ?? stats.l ?? stats.loss;
  const out = [];
  if (Number.isFinite(w)) out.push({ label: 'Wins', shortLabel: 'W', value: Number(w) });
  if (Number.isFinite(d)) out.push({ label: 'Draws', shortLabel: 'D', value: Number(d) });
  if (Number.isFinite(l)) out.push({ label: 'Losses', shortLabel: 'L', value: Number(l) });
  return out;
};

const TeamStats = ({ team, stats }) => {
  const { theme } = useThemeProvider();
  const { width } = useWindowSize();

  const coverSrc = team?.coverimg || team?.trikot?.home || fallbackCover;
  const teamLogo = team?.logo || fallbackLogo;

  const normalized = normalizeStats(stats);
  const data =
    normalized.length > 0
      ? normalized
      : Number.isFinite(Number(team?.points))
        ? [{ label: 'Points', shortLabel: 'Pts', value: Number(team.points) }]
        : [];

  const pickLabel = (label, shortLabel) => {
    if (width < 1024) return shortLabel;
    if (width >= 1500 && width < 1920) return shortLabel || label;
    return label;
  };

  return (
    <Spring
      className={`${styles.container} ${theme === 'light' ? styles.light : styles.dark} card no-shadow card-padded text-black`}
    >
      <img className={`${styles.cover} cover`} src={coverSrc} alt="team cover" />
      <div className={`${styles.content} d-flex flex-column align-items-start justify-content-between h-100`}>
        <img className="club-logo" src={teamLogo} alt={`${team?.team_name || 'Club'} logo`} />
        <div className={`${styles.content_header} d-flex flex-column g-4 flex-1`}>
          <h2 className={`${styles.club} text-20 text-black text-overflow`}>
            {team?.team_name || 'Teamname'}
          </h2>
          {/* <h4 className="text-black text-overflow">Optional subtitle here</h4> */}
        </div>

        {data.length > 0 && (
          <div className="d-flex flex-wrap g-20">
            {data.map((item, index) => (
              <StatsBadge
                key={`${item.label}-${index}`}
                label={pickLabel(item.label, item.shortLabel)}
                value={item.value}
              />
            ))}
          </div>
        )}
      </div>
    </Spring>
  );
};

export default TeamStats;

/*
Usage:

<TeamStats team={team} />

<TeamStats team={team} stats={{ wins: 12, draws: 5, losses: 2 }} />

<TeamStats
  team={team}
  stats={[
    { label: 'Wins', shortLabel: 'W', value: 12 },
    { label: 'Draws', shortLabel: 'D', value: 5 },
    { label: 'Losses', shortLabel: 'L', value: 2 },
  ]}
/>
*/
